import { ref } from 'vue'
import jwtService from '@/app/services/jwt.service'

export const REFRESH_TOKEN_EXPIRY_KEY = 'refreshTokenExpiryTime'

const REFRESH_BUFFER_MS = 60 * 1000
export const DEFAULT_REFRESH_POLL_MS = 8 * 60 * 1000
export const MAX_SETTIMEOUT_MS = 2147483647

export const sessionRevision = ref(0)

/** Bump after sessionStorage auth keys change so session computeds re-read them. */
export function touchSession(): void {
  sessionRevision.value += 1
}

/** Call inside a computed. sessionStorage itself is not reactive. */
export function trackStoredSession(): number {
  return sessionRevision.value
}

export function persistRefreshTokenExpiryTime(expiryTime: string | number | null | undefined): void {
  if (expiryTime === null || expiryTime === undefined || expiryTime === '') return
  sessionStorage.setItem(REFRESH_TOKEN_EXPIRY_KEY, String(expiryTime))
}

export function parseExpiryMs(value: string | null | undefined): number | null {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null

  if (/^\d+$/.test(trimmed)) {
    const numeric = Number(trimmed)
    if (!Number.isFinite(numeric)) return null
    return trimmed.length <= 10 ? numeric * 1000 : numeric
  }

  const normalized = trimmed.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')
  const parsed = new Date(normalized).getTime()
  return Number.isNaN(parsed) ? null : parsed
}

export function readAccessTokenExpiryMs(accessToken: string | null | undefined): number | null {
  if (!accessToken) return null
  const parts = accessToken.split('.')
  if (parts.length < 2 || !parts[1]) return null

  try {
    const payload = JSON.parse(decodeBase64Url(parts[1])) as { exp?: unknown }
    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return null
    return payload.exp * 1000
  } catch {
    return null
  }
}

/**
 * Wait until shortly before the access token expires.
 * A far-future refresh-token expiry is not that deadline: poll on the default
 * interval instead, and only pull the refresh forward when the refresh token
 * itself expires inside that window.
 */
export function computeRefreshDelayMs(input: {
  now: number
  accessToken: string | null
  refreshTokenExpiry: string | null
}): number {
  const accessExpiry = readAccessTokenExpiryMs(input.accessToken)
  const refreshExpiry = parseExpiryMs(input.refreshTokenExpiry)

  if (accessExpiry != null) {
    const deadline = refreshExpiry != null ? Math.min(accessExpiry, refreshExpiry) : accessExpiry
    return Math.max(deadline - input.now - REFRESH_BUFFER_MS, 0)
  }

  if (refreshExpiry != null) {
    const untilRefreshExpiry = refreshExpiry - input.now - REFRESH_BUFFER_MS
    if (untilRefreshExpiry < DEFAULT_REFRESH_POLL_MS) {
      return Math.max(untilRefreshExpiry, 0)
    }
  }

  return DEFAULT_REFRESH_POLL_MS
}

export function getRefreshDelayMs(now = Date.now()): number {
  return computeRefreshDelayMs({
    now,
    accessToken: readStoredAccessToken(),
    refreshTokenExpiry: sessionStorage.getItem(REFRESH_TOKEN_EXPIRY_KEY),
  })
}

function readStoredAccessToken(): string | null {
  try {
    const token = jwtService.getToken()
    if (typeof token === 'string' && token) return token
  } catch {
    // ignore malformed session storage
  }
  return sessionStorage.getItem('token')
}

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}
